"use client";

import { forwardRef, type ReactNode } from "react";
import { Overlay, type OverlayProps } from "./Overlay";

export type DialogProps = Omit<OverlayProps, "children" | "role" | "ariaModal" | "ariaLabel" | "ariaLabelledBy" | "ariaDescribedBy" | "panelClassName"> & {
  children: ReactNode;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};

const DEFAULT_OVERLAY_CLASS_NAME = "fixed inset-0 z-[200] flex items-center justify-center p-4";
const DEFAULT_PANEL_CLASS_NAME =
  "relative z-10 max-h-[min(90vh,48rem)] w-full max-w-lg overflow-auto rounded-[var(--fz-radius)] border border-[var(--fz-overlay-surface-border)] bg-[var(--fz-overlay-surface-bg)] p-6 text-[var(--fz-overlay-surface-text)] shadow-[var(--fz-overlay-surface-shadow)] outline-none";

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    children,
    className,
    overlayClassName,
    backdropClassName,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...behavior
  },
  ref,
) {
  return (
    <Overlay
      ref={ref}
      {...behavior}
      ariaDescribedBy={ariaDescribedBy}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      ariaModal
      backdropClassName={backdropClassName}
      overlayClassName={overlayClassName ?? DEFAULT_OVERLAY_CLASS_NAME}
      panelClassName={className ?? DEFAULT_PANEL_CLASS_NAME}
      role="dialog"
    >
      {children}
    </Overlay>
  );
});
